import { ApiService } from '../../../services/apiService';

// Tourist Visa API endpoints (placeholder - to be implemented in Flask backend)
const _TOURIST_VISA_ENDPOINTS = {
    CHAT: '/api/domain/tourist_visa/chat/message',
    UPLOAD: '/api/domain/tourist_visa/upload',
    QUERY: '/api/domain/tourist_visa/query',
    INFO: '/api/domain/tourist_visa/info',
    CLEAR: '/api/domain/tourist_visa/clear'
};

// Interface for chat message request
export interface TouristVisaChatRequest {
    message: string;
    previous_response_id?: string;
}

// Interface for chat message response
export interface TouristVisaChatResponse {
    response: string;
    response_id: string;
    status: 'success' | 'error';
    user?: {
        uid: string;
        email: string;
    };
}

// Interface for document query request
export interface TouristVisaQueryRequest {
    query: string;
    custom_prompt?: string;
    search_kwargs?: {
        k?: number;
        fetch_k?: number;
    };
}

// Interface for document query response
export interface TouristVisaQueryResponse {
    answer: string;
    source_documents: Array<{
        page_content: string;
        metadata: {
            source: string;
            page: number;
        };
    }>;
    status: 'success' | 'error';
}

// Interface for document info response
export interface TouristVisaDocumentInfo {
    document_info: {
        total_documents: number;
        total_chunks: number;
        collection_name: string;
    };
    status: 'success' | 'error';
}

// Tourist Visa Service class extending the centralized API service
class TouristVisaService extends ApiService {
    // Send a chat message to the Tourist Visa AI assistant
    async sendChatMessage(request: TouristVisaChatRequest): Promise<TouristVisaChatResponse> {
        // TODO: Implement when Flask backend tourist visa endpoints are ready
        // For now, return a placeholder response
        return {
            response: `I received your message: "${request.message}". Tourist Visa AI assistant is coming soon!`,
            response_id: `placeholder_${Date.now()}`,
            status: 'success'
        };

        // Uncomment when backend is ready:
        // return this.post<TouristVisaChatResponse>(TOURIST_VISA_ENDPOINTS.CHAT, request);
    }

    // Upload and process Tourist Visa documents
    async uploadDocuments(): Promise<TouristVisaDocumentInfo> {
        // TODO: Implement when Flask backend tourist visa endpoints are ready
        throw new Error('Tourist Visa document upload not yet implemented');

        // Uncomment when backend is ready:
        // return this.post<TouristVisaDocumentInfo>(TOURIST_VISA_ENDPOINTS.UPLOAD);
    }

    // Query Tourist Visa documents
    async queryDocuments(_request: TouristVisaQueryRequest): Promise<TouristVisaQueryResponse> {
        // TODO: Implement when Flask backend tourist visa endpoints are ready
        throw new Error('Tourist Visa document query not yet implemented');

        // Uncomment when backend is ready:
        // return this.post<TouristVisaQueryResponse>(TOURIST_VISA_ENDPOINTS.QUERY, request);
    }

    // Get Tourist Visa document information
    async getDocumentInfo(): Promise<TouristVisaDocumentInfo> {
        // TODO: Implement when Flask backend tourist visa endpoints are ready
        throw new Error('Tourist Visa document info not yet implemented');

        // Uncomment when backend is ready:
        // return this.get<TouristVisaDocumentInfo>(TOURIST_VISA_ENDPOINTS.INFO);
    }

    // Clear Tourist Visa vector database
    async clearDatabase(): Promise<{ message: string; status: string }> {
        // TODO: Implement when Flask backend tourist visa endpoints are ready
        throw new Error('Tourist Visa database clear not yet implemented');

        // Uncomment when backend is ready:
        // return this.post<{ message: string; status: string }>(TOURIST_VISA_ENDPOINTS.CLEAR);
    }
}

// Create and export a singleton instance
export const touristVisaService = new TouristVisaService();

// Export individual functions for backward compatibility
export const sendTouristVisaChatMessage = (request: TouristVisaChatRequest): Promise<TouristVisaChatResponse> =>
    touristVisaService.sendChatMessage(request);

export const uploadTouristVisaDocuments = (): Promise<TouristVisaDocumentInfo> =>
    touristVisaService.uploadDocuments();

export const queryTouristVisaDocuments = (request: TouristVisaQueryRequest): Promise<TouristVisaQueryResponse> =>
    touristVisaService.queryDocuments(request);

export const getTouristVisaDocumentInfo = (): Promise<TouristVisaDocumentInfo> =>
    touristVisaService.getDocumentInfo();

export const clearTouristVisaDatabase = (): Promise<{ message: string; status: string }> =>
    touristVisaService.clearDatabase(); 